/**
 * License detail page - full information for a specific license
 */
import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom'
import { format, addDays, isBefore } from 'date-fns'
import { formatLocalDate } from '../utils/formatDateTime'
import { getLicenseByCallSign, getEntitiesByCallSign, getLocationsByCallSign, getFrequenciesByCallSign } from '../services/licenses'
import { convertLead, getLicenseCrmStatus, checkDnc } from '../services/crm'
import type { FCCLicense, FCCEntity, FCCLocation, FCCFrequency } from '../types'
import { LICENSE_STATUS, RADIO_SERVICES } from '../types'
import AddLeadModal from '../components/AddLeadModal'
import InfoHint from '../components/InfoHint'
import DncToggle, { DncBadge } from '../components/DncToggle'
import EntityCard from '../components/EntityCard'

// Station class code → human-readable label (common LM codes)
const CLASS_STATION_LABELS: Record<string, string> = {
  FXO: 'Operational Fixed',
  FB:  'Fixed Base',
  FB2: 'Fixed Base (2-way)',
  FB2C: 'Mobile Relay - Interconnect',
  FB4: 'Base (Dispatch)',
  FB6: 'Base (Remote)',
  FB8: 'Base (Repeater)',
  MO:  'Mobile',
  MO1: 'Mobile (Portable)',
  MO2: 'Mobile (Vehicle)',
  FA:  'Fixed Auxiliary',
  FX:  'Fixed',
  YM:  'Microwave',
  TP:  'Temporary',
}

function getExpirationClass(dateStr: string | null): string {
  if (!dateStr) return ''
  const expiry = new Date(dateStr)
  const now = new Date()
  if (isBefore(expiry, now)) return 'text-red-600 font-semibold'
  if (isBefore(expiry, addDays(now, 90))) return 'text-orange-600 font-medium'
  return ''
}

function getStatusBadgeClass(status: string): string {
  if (status === 'A') return 'bg-green-100 text-green-800'
  if (status === 'E' || status === 'C') return 'bg-red-100 text-red-800'
  return 'bg-gray-100 text-gray-800'
}

function formatDMS(
  deg: number | null | undefined,
  min: number | null | undefined,
  sec: number | null | undefined,
  dir: string | null | undefined,
): string {
  if (deg == null) return '—'
  const parts = [`${deg}°`]
  if (min != null) parts.push(`${min}'`)
  if (sec != null) parts.push(`${sec.toFixed(1)}"`)
  if (dir) parts.push(dir)
  return parts.join(' ')
}

function formatFreq(mhz: number | null): string {
  if (mhz == null) return '—'
  return `${mhz.toFixed(5)} MHz`
}

function formatPower(watts: number | null): string {
  if (watts == null) return '—'
  return `${watts} W`
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—'
  try {
    return format(new Date(dateStr), 'MM/dd/yyyy')
  } catch {
    return dateStr
  }
}

function classStationLabel(code: string | null): string {
  if (!code) return '—'
  return CLASS_STATION_LABELS[code] ? `${code} — ${CLASS_STATION_LABELS[code]}` : code
}

function FrequencyTable({ frequencies }: { frequencies: FCCFrequency[] }) {
  if (frequencies.length === 0) return null

  // Group by antenna number
  const byAntenna = new Map<number, FCCFrequency[]>()
  for (const f of frequencies) {
    const ant = f.antenna_number ?? 1
    if (!byAntenna.has(ant)) byAntenna.set(ant, [])
    byAntenna.get(ant)!.push(f)
  }
  const antennaNums = Array.from(byAntenna.keys()).sort((a, b) => a - b)

  return (
    <div className="mt-4 space-y-4">
      {antennaNums.map((antNum) => {
        const antFreqs = byAntenna.get(antNum)!
        return (
          <div key={antNum}>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Antenna {antNum}
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-1 pr-4 text-xs font-medium text-gray-500">Frequency</th>
                    <th className="text-left py-1 pr-4 text-xs font-medium text-gray-500">Power</th>
                    <th className="text-left py-1 pr-4 text-xs font-medium text-gray-500">ERP</th>
                    <th className="text-left py-1 pr-4 text-xs font-medium text-gray-500">Emission</th>
                    <th className="text-left py-1 pr-4 text-xs font-medium text-gray-500">Class</th>
                    {antFreqs.some(f => f.units_of_work != null) && (
                      <th className="text-left py-1 pr-4 text-xs font-medium text-gray-500">Units</th>
                    )}
                    {antFreqs.some(f => f.transmit_receive_code) && (
                      <th className="text-left py-1 pr-4 text-xs font-medium text-gray-500">T/R</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {antFreqs.map((f) => (
                    <tr key={f.id} className="border-b border-gray-100 last:border-0">
                      <td className="py-1.5 pr-4 font-mono text-gray-900">
                        {formatFreq(f.frequency)}
                        {f.frequency_upper_band != null && (
                          <div className="text-xs text-gray-400">
                            Upper: {formatFreq(f.frequency_upper_band)}
                          </div>
                        )}
                      </td>
                      <td className="py-1.5 pr-4 text-gray-700">{formatPower(f.power_output)}</td>
                      <td className="py-1.5 pr-4 text-gray-700">{formatPower(f.power_erp)}</td>
                      <td className="py-1.5 pr-4 font-mono text-gray-900">{f.emission_code || '—'}</td>
                      <td className="py-1.5 pr-4 text-gray-700">{classStationLabel(f.class_station_code)}</td>
                      {antFreqs.some(ff => ff.units_of_work != null) && (
                        <td className="py-1.5 pr-4 text-gray-700">
                          {f.units_of_work != null ? f.units_of_work : '—'}
                        </td>
                      )}
                      {antFreqs.some(ff => ff.transmit_receive_code) && (
                        <td className="py-1.5 pr-4 text-gray-700">{f.transmit_receive_code || '—'}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* First-used date (if populated) */}
            {antFreqs.some(f => f.date_first_used) && (
              <div className="mt-1.5 flex flex-wrap gap-4 text-xs text-gray-500">
                {antFreqs[0].date_first_used && (
                  <span>First used: <span className="text-gray-700">{formatDate(antFreqs[0].date_first_used)}</span></span>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function BackArrowIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
    </svg>
  )
}

export default function LicenseDetailPage() {
  const { callSign } = useParams<{ callSign: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  // This page is routinely opened in a NEW TAB — the client detail page links each
  // license with target="_blank" so in-progress notes aren't lost. A fresh tab has
  // no history entry of ours, so navigate(-1) is a silent no-op and "Back" becomes a
  // dead control. React Router stamps the first entry of a session with key 'default';
  // window.history.length is the belt-and-braces fallback for a hard reload.
  const canGoBack = location.key !== 'default' && window.history.length > 1

  const [license, setLicense] = useState<FCCLicense | null>(null)
  const [entities, setEntities] = useState<FCCEntity[]>([])
  const [locations, setLocations] = useState<FCCLocation[]>([])
  const [frequencies, setFrequencies] = useState<FCCFrequency[]>([])
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [error, setError] = useState('')

  // CRM action state
  const [actionStatus, setActionStatus] = useState<'idle' | 'lead' | 'client' | 'loading' | 'error'>('idle')
  const [actionError, setActionError] = useState('')
  const [crmLeadId, setCrmLeadId] = useState<number | null>(null)
  const [crmClientId, setCrmClientId] = useState<number | null>(null)
  const [leadModal, setLeadModal] = useState(false)

  // Do Not Contact for this call sign. Previously settable only from search
  // results, so a user who navigated straight to a license had no way to flag it.
  const [isDnc, setIsDnc] = useState(false)
  const [dncToast, setDncToast] = useState('')

  useEffect(() => {
    if (!callSign) return
    loadLicense(callSign)
  }, [callSign])

  async function loadLicense(cs: string) {
    setLoading(true)
    setError('')
    setNotFound(false)

    try {
      const [licenseData, entitiesData, locationData, frequencyData] = await Promise.allSettled([
        getLicenseByCallSign(cs),
        getEntitiesByCallSign(cs),
        getLocationsByCallSign(cs),
        getFrequenciesByCallSign(cs),
      ])

      if (licenseData.status === 'rejected') {
        const msg = licenseData.reason?.message ?? ''
        if (msg.includes('404') || msg.toLowerCase().includes('not found')) {
          setNotFound(true)
        } else {
          setError(msg || 'Failed to load license')
        }
        return
      }

      setLicense(licenseData.value)

      if (entitiesData.status === 'fulfilled') {
        setEntities(entitiesData.value)
      }

      if (locationData.status === 'fulfilled') {
        setLocations(locationData.value)
      }

      if (frequencyData.status === 'fulfilled') {
        setFrequencies(frequencyData.value)
      }

      // Check if this license is already tracked in CRM (non-fatal)
      try {
        const crmStatus = await getLicenseCrmStatus(licenseData.value.unique_system_identifier)
        if (crmStatus.is_client) {
          setActionStatus('client')
          setCrmClientId(crmStatus.client_id)
        } else if (crmStatus.is_lead) {
          setActionStatus('lead')
          setCrmLeadId(crmStatus.lead_id)
        }
      } catch {
        // CRM status is display-only; don't fail the page
      }

      // Do Not Contact flag for this call sign (also display-only, also non-fatal)
      try {
        const dnc = await checkDnc('call_sign', licenseData.value.call_sign)
        setIsDnc(dnc.is_dnc)
      } catch {
        // Leave it unflagged rather than failing the page.
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load license')
    } finally {
      setLoading(false)
    }
  }

  async function handleAddAsClient() {
    if (!license?.unique_system_identifier) return
    setActionStatus('loading')
    setActionError('')
    try {
      const client = await convertLead(license.unique_system_identifier)
      setCrmClientId(client.id)
      setActionStatus('client')
    } catch (err) {
      setActionStatus('error')
      setActionError(err instanceof Error ? err.message : 'Failed to add client')
    }
  }

  if (loading) {
    return <div className="text-center py-8 text-gray-500">Loading...</div>
  }

  if (notFound) {
    return (
      <div className="card text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          License {callSign} not found
        </h2>
        <p className="text-gray-500 mb-6">
          No license with this call sign exists in the database.
        </p>
        <Link to="/search" className="btn btn-primary">
          Back to Search
        </Link>
      </div>
    )
  }

  if (error) {
    return <div className="alert alert-error">{error}</div>
  }

  if (!license) return null

  // Primary entity for header display (first in list, or license.entity)
  const primaryEntity = entities[0] ?? license.entity

  // Group frequencies by location_number for nested display
  const freqsByLocation = new Map<number, FCCFrequency[]>()
  for (const f of frequencies) {
    const loc = f.location_number ?? 0
    if (!freqsByLocation.has(loc)) freqsByLocation.set(loc, [])
    freqsByLocation.get(loc)!.push(f)
  }

  return (
    <div>
      <div className="mb-4">
        {canGoBack ? (
          <button
            onClick={() => navigate(-1)}
            aria-label="Go back"
            className="inline-flex items-center gap-1.5 text-primary-600 hover:text-primary-800 text-sm font-medium py-1"
          >
            <BackArrowIcon />
            Back
          </button>
        ) : (
          <Link
            to="/search"
            className="inline-flex items-center gap-1.5 text-primary-600 hover:text-primary-800 text-sm font-medium py-1"
          >
            <BackArrowIcon />
            Back to Search
          </Link>
        )}
      </div>

      {/* DNC toast notification (same presentation as search results) */}
      {dncToast && (
        <div className="fixed bottom-4 right-4 z-50 bg-gray-800 text-white text-sm px-4 py-2.5 rounded shadow-lg" role="status">
          {dncToast}
        </div>
      )}

      {/* Header: call sign + state + status + actions */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex flex-wrap items-center gap-3 mb-1">
            <h1 className="text-3xl font-bold text-gray-900">{license.call_sign}</h1>
            <span
              className={`px-2.5 py-1 text-sm rounded-full font-medium ${getStatusBadgeClass(license.license_status)}`}
            >
              {LICENSE_STATUS[license.license_status] || license.license_status}
            </span>
            {license.has_pending_application && (
              <span
                title="This licensee has already filed a renewal application with the FCC"
                className="px-2.5 py-1 text-sm rounded-full font-medium bg-blue-100 text-blue-700"
              >
                Renewal Pending
              </span>
            )}
            {isDnc && <DncBadge className="px-2.5 py-1 text-sm rounded-full" />}
            {license.purged_at && (
              <span
                title={`This license is no longer in the current FCC data (as of ${formatLocalDate(license.purged_at)}). The record is retained for reference.`}
                className="px-2.5 py-1 text-sm rounded-full font-medium bg-amber-100 text-amber-700"
              >
                Not in current FCC data
              </span>
            )}
          </div>
          {primaryEntity?.state && (
            <p className="text-lg text-gray-600">
              {primaryEntity.city ? `${primaryEntity.city}, ` : ''}{primaryEntity.state}
              {primaryEntity.entity_name && (
                <span className="ml-2 text-gray-400 text-base">— {primaryEntity.entity_name}</span>
              )}
            </p>
          )}
        </div>

        {/* CRM action buttons */}
        <div className="toolbar gap-2 sm:flex-shrink-0">
          <Link
            to={`/licenses/${license.call_sign}/reference`}
            className="btn btn-secondary text-sm"
            title="View the full FCC Reference Copy (printable)"
          >
            Reference Copy
          </Link>
          <DncToggle
            variant="button"
            callSign={license.call_sign}
            isDnc={isDnc}
            onChange={setIsDnc}
            onMessage={(msg) => {
              setDncToast(msg)
              setTimeout(() => setDncToast(''), 3000)
            }}
            onError={setActionError}
          />
          {actionStatus === 'lead' ? (
            crmLeadId ? (
              <Link
                to={`/crm/leads/${crmLeadId}`}
                className="text-sm text-green-700 font-medium bg-green-50 px-3 py-1.5 rounded hover:bg-green-100 transition-colors"
              >
                ✓ Added as lead →
              </Link>
            ) : (
              <span className="text-sm text-green-700 font-medium bg-green-50 px-3 py-1.5 rounded">
                ✓ Added as lead
              </span>
            )
          ) : actionStatus === 'client' ? (
            crmClientId ? (
              <Link
                to={`/crm/clients/${crmClientId}`}
                className="text-sm text-green-700 font-medium bg-green-50 px-3 py-1.5 rounded hover:bg-green-100 transition-colors"
              >
                ✓ Added as client →
              </Link>
            ) : (
              <span className="text-sm text-green-700 font-medium bg-green-50 px-3 py-1.5 rounded">
                ✓ Added as client
              </span>
            )
          ) : (
            <>
              {actionStatus === 'error' && (
                <span className="text-sm text-red-600">{actionError || 'Action failed'}</span>
              )}
              <button
                type="button"
                disabled={actionStatus === 'loading'}
                onClick={() => setLeadModal(true)}
                className="btn btn-secondary text-sm"
              >
                + Lead
              </button>
              <button
                type="button"
                disabled={actionStatus === 'loading'}
                onClick={handleAddAsClient}
                className="btn btn-secondary text-sm"
              >
                {actionStatus === 'loading' ? '…' : '+ Client'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="space-y-6">
        {/* License Information */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-4">License Information</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Call Sign</dt>
              <dd className="mt-1 text-sm text-gray-900 font-mono font-semibold">
                {license.call_sign}
              </dd>
            </div>

            <div>
              <dt className="text-sm font-medium text-gray-500">Status</dt>
              <dd className="mt-1">
                <span
                  className={`px-2 py-1 text-xs rounded-full ${getStatusBadgeClass(
                    license.license_status
                  )}`}
                >
                  {LICENSE_STATUS[license.license_status] || license.license_status}
                </span>
              </dd>
            </div>

            <div>
              <dt className="text-sm font-medium text-gray-500">Service Type</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {RADIO_SERVICES[license.radio_service_code]
                  ? `${license.radio_service_code} — ${RADIO_SERVICES[license.radio_service_code]}`
                  : license.radio_service_code}
              </dd>
            </div>

            <div>
              <dt className="text-sm font-medium text-gray-500">Grant Date</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {license.grant_date
                  ? format(new Date(license.grant_date), 'MM/dd/yyyy')
                  : 'N/A'}
              </dd>
            </div>

            <div>
              <dt className="text-sm font-medium text-gray-500">Expiration Date</dt>
              <dd className={`mt-1 text-sm ${getExpirationClass(license.expired_date)}`}>
                {license.expired_date
                  ? format(new Date(license.expired_date), 'MM/dd/yyyy')
                  : 'N/A'}
              </dd>
            </div>

            <div>
              <dt className="text-sm font-medium text-gray-500 flex items-center gap-1">
                Last Action Date
                <InfoHint label="What is the last action date?">
                  The date of the FCC's most recent action on this license, as the FCC records
                  it: for example a grant, a renewal, a change, or an accepted filing. It does
                  not say what the action was, so it can't tell you whether a construction
                  notification was filed.
                </InfoHint>
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {license.last_action_date
                  ? format(new Date(license.last_action_date), 'MM/dd/yyyy')
                  : 'N/A'}
              </dd>
            </div>
          </dl>
        </div>

        {/* Entities section (all entity types) */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-4">
            Licensee Information
            {entities.length > 1 && (
              <span className="ml-2 text-sm font-normal text-gray-500">({entities.length} records)</span>
            )}
          </h2>
          {entities.length === 0 && !license.entity ? (
            <p className="text-gray-500 text-sm">No entity information on file.</p>
          ) : entities.length > 0 ? (
            <div className="space-y-4">
              {entities.map((e, idx) => (
                <EntityCard key={e.id} entity={e} isPrimary={idx === 0} />
              ))}
            </div>
          ) : license.entity ? (
            <EntityCard entity={license.entity} isPrimary={true} />
          ) : null}
        </div>

        {/* Transmitter Locations with nested frequencies */}
        {locations.length > 0 && (
          <div className="card">
            <h2 className="text-xl font-semibold mb-4">
              Transmitter Location{locations.length > 1 ? 's' : ''}
              {locations.length > 1 && (
                <span className="ml-2 text-sm font-normal text-gray-500">({locations.length})</span>
              )}
            </h2>
            <div className="space-y-6">
              {locations.map((loc) => {
                const locFreqs = freqsByLocation.get(loc.location_number) ?? []
                return (
                  <div key={loc.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs font-semibold bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                        Tx #{loc.location_number}
                      </span>
                      {loc.location_name && (
                        <span className="text-sm font-medium text-gray-900">{loc.location_name}</span>
                      )}
                      {loc.location_type_code && (
                        <span className="text-xs text-gray-400">Type: {loc.location_type_code}</span>
                      )}
                    </div>
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                      {(loc.location_address || loc.location_city || loc.location_county || loc.location_state) && (
                        <div>
                          <dt className="text-xs font-medium text-gray-500">Address</dt>
                          <dd className="mt-0.5 text-sm text-gray-900">
                            {loc.location_address && <span>{loc.location_address}<br /></span>}
                            {[loc.location_city, loc.location_county && `${loc.location_county} Co.`, loc.location_state]
                              .filter(Boolean).join(', ')}
                          </dd>
                        </div>
                      )}

                      {(loc.lat_degrees != null || loc.long_degrees != null) && (
                        <div>
                          <dt className="text-xs font-medium text-gray-500">Coordinates</dt>
                          <dd className="mt-0.5 text-sm text-gray-900 font-mono">
                            <div>{formatDMS(loc.lat_degrees, loc.lat_minutes, loc.lat_seconds, loc.lat_direction)}</div>
                            <div>{formatDMS(loc.long_degrees, loc.long_minutes, loc.long_seconds, loc.long_direction)}</div>
                          </dd>
                        </div>
                      )}

                      {loc.ground_elevation != null && (
                        <div>
                          <dt className="text-xs font-medium text-gray-500">Ground Elevation</dt>
                          <dd className="mt-0.5 text-sm text-gray-900">{loc.ground_elevation} m</dd>
                        </div>
                      )}

                      {loc.height_of_support_structure != null && (
                        <div>
                          <dt className="text-xs font-medium text-gray-500">Support Structure Height</dt>
                          <dd className="mt-0.5 text-sm text-gray-900">{loc.height_of_support_structure} m</dd>
                        </div>
                      )}

                      {loc.tower_registration_number && (
                        <div>
                          <dt className="text-xs font-medium text-gray-500">Tower Registration</dt>
                          <dd className="mt-0.5 text-sm text-gray-900 font-mono">{loc.tower_registration_number}</dd>
                        </div>
                      )}

                      {loc.radius_of_operation != null && (
                        <div>
                          <dt className="text-xs font-medium text-gray-500">Radius of Operation</dt>
                          <dd className="mt-0.5 text-sm text-gray-900">{loc.radius_of_operation} km</dd>
                        </div>
                      )}

                      {loc.area_of_operation_code && (
                        <div>
                          <dt className="text-xs font-medium text-gray-500">Area of Operation</dt>
                          <dd className="mt-0.5 text-sm text-gray-900">{loc.area_of_operation_code}</dd>
                        </div>
                      )}
                    </dl>

                    {/* Frequency table nested under this location */}
                    {locFreqs.length > 0 && (
                      <div className="mt-4 pt-3 border-t border-gray-100">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                          Frequencies
                        </div>
                        <FrequencyTable frequencies={locFreqs} />
                      </div>
                    )}
                  </div>
                )
              })}

              {/* Frequencies not matched to a known location (e.g., location_number 0 or null) */}
              {(() => {
                const unmatched = frequencies.filter(
                  f => !locations.some(l => l.location_number === (f.location_number ?? 0))
                )
                if (unmatched.length === 0) return null
                return (
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs font-semibold bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                        Frequencies (unmatched location)
                      </span>
                    </div>
                    <FrequencyTable frequencies={unmatched} />
                  </div>
                )
              })()}
            </div>
          </div>
        )}

        {/* Frequencies with no location data */}
        {locations.length === 0 && frequencies.length > 0 && (
          <div className="card">
            <h2 className="text-xl font-semibold mb-4">Frequencies</h2>
            <FrequencyTable frequencies={frequencies} />
          </div>
        )}
      </div>

      {/* Add as Lead modal */}
      {leadModal && license.unique_system_identifier && (
        <AddLeadModal
          usi={license.unique_system_identifier}
          subtitle={`${license.call_sign} — ${primaryEntity?.entity_name || 'Unknown entity'}`}
          onClose={() => setLeadModal(false)}
          onAdded={(lead) => {
            setCrmLeadId(lead.id)
            setActionStatus('lead')
            setLeadModal(false)
          }}
        />
      )}
    </div>
  )
}
